import { z } from "zod";
import type { BulkApplyMergeEntry } from "./bulk-apply-merge-entry";

const E2E_COMMENT_ID_SCHEMA = z.union([z.string(), z.number()]).transform((value) => String(value));

export const E2E_COMMENT_SNAPSHOT_SCHEMA = z.object({
  id: E2E_COMMENT_ID_SCHEMA,
  text: z.string(),
  replies: z.array(z.object({ id: E2E_COMMENT_ID_SCHEMA, text: z.string() })),
});

export type E2eCommentSnapshot = z.infer<typeof E2E_COMMENT_SNAPSHOT_SCHEMA>;

export const E2eCommentIdentitySchema = z.object({
  officeJs: z.array(E2E_COMMENT_SNAPSHOT_SCHEMA),
  packageParts: z.array(z.string()),
  ooxmlComments: z.array(
    z.object({
      wId: z.string().nullable(),
      lastParagraphParaId: z.string().nullable(),
      text: z.string(),
    }),
  ),
  commentsExtended: z.array(
    z.object({
      paraId: z.string().nullable(),
      paraIdParent: z.string().nullable(),
      done: z.string().nullable(),
    }),
  ),
  commentsIds: z.array(
    z.object({ paraId: z.string().nullable(), durableId: z.string().nullable() }),
  ),
});

export type E2eCommentIdentity = z.infer<typeof E2eCommentIdentitySchema>;

export interface E2eBulkMergeErrorReport {
  errorMessage: string;
  causeMessage?: string;
  context: unknown;
}

export interface E2eBulkMergeDropCount {
  reason: string;
  count: number;
}

export interface E2eBulkMergeReplay {
  failedRuleIds: string[];
  selected: number;
  attempted: number;
  drops: E2eBulkMergeDropCount[];
  errorReports: E2eBulkMergeErrorReport[];
}

export type E2eWordOps = {
  insertParagraph(text: string): Promise<void>;
  insertParagraphs(texts: string[]): Promise<void>;
  clear(): Promise<void>;
  selectText(query: string): Promise<boolean>;
  getBodyText(): Promise<string>;
  getTrackedChangeAuthors(): Promise<string[]>;
  insertComment(
    anchorText: string,
    commentText: string,
    rangeEndAnchorText?: string,
  ): Promise<string | null>;
  replyToComment(targetCommentId: string, replyText: string): Promise<boolean>;
  resolveComment(targetCommentId: string): Promise<boolean>;
  getComments(): Promise<E2eCommentSnapshot[]>;
  readCommentIdentity(): Promise<E2eCommentIdentity>;
  applyBulkMergeData(mergeData: BulkApplyMergeEntry[]): Promise<E2eBulkMergeReplay>;
  setCustomProperties(properties: Record<string, string>): Promise<void>;
  deleteCustomProperties(propertyNames: string[]): Promise<void>;
  dismissToasts(): void;
  selectConversation(conversationId: string): void;
  currentConversationId: string | undefined;
  currentSelectionText: string | undefined;
};

declare global {
  interface Window {
    __e2eWordOps?: E2eWordOps;
  }
}
